import { useCallback, useEffect, useRef, useState } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { LoginResponse } from '../../types/auth'
import type { StatusMessage } from '../../types/status'
import { getAuthSetupRequired } from '../../api/authApi'
import { checkAuthSession } from '../../api/auth/sessionCheck'
import type { AuthSessionCheckResult } from '../../api/auth/sessionCheck'
import { SESSION_CHECK_RETRY_INTERVAL_MS } from '../../constants/authSession'
import { clearLegacyAuthStorage, resolveApiErrorMessage } from './shared'

interface UseAuthSessionBootstrapParams {
  apiBaseUrl: string
  navigate: NavigateFunction
  pathname: string
  isAuthenticated: boolean
  isAdmin: boolean
  clearAuthState: () => void
  applyAuthUserData: (userData: Partial<LoginResponse>, fallbackUserName?: string) => {
    userName: string
    userEmail: string
    mustChangePassword: boolean
  }
  resetProfileForm: (userName?: string, userEmail?: string) => void
  setSetupRequired: (value: boolean) => void
  setAuthStatus: (status: StatusMessage | null) => void
  setProfileStatus: (status: StatusMessage | null) => void
  setIsAuthMetaLoaded: (value: boolean) => void
}

export interface UseAuthSessionBootstrapResult {
  isSessionCheckFailed: boolean
  sessionCheckErrorMessage: string | null
  isSessionCheckRetrying: boolean
  retrySessionCheck: () => void
}

interface SessionCheckFailure {
  message: string
}

interface PendingSessionCheck {
  apiBaseUrl: string
  promise: Promise<AuthSessionCheckResult>
}

function logUnhandledAuthEffectError(error: unknown): void {
  if (!import.meta.env.DEV) return
  console.error('Unhandled auth initialization error:', error)
}

export function useAuthSessionBootstrap({
  apiBaseUrl,
  navigate,
  pathname,
  isAuthenticated,
  isAdmin,
  clearAuthState,
  applyAuthUserData,
  resetProfileForm,
  setSetupRequired,
  setAuthStatus,
  setProfileStatus,
  setIsAuthMetaLoaded
}: UseAuthSessionBootstrapParams): UseAuthSessionBootstrapResult {
  const [sessionCheckFailure, setSessionCheckFailure] = useState<SessionCheckFailure | null>(null)
  const [isSessionCheckRetrying, setIsSessionCheckRetrying] = useState(false)
  const generationRef = useRef(0)
  const pendingCheckRef = useRef<PendingSessionCheck | null>(null)
  const retryGenerationRef = useRef<number | null>(null)

  const startSessionCheck = useCallback((baseUrl: string): Promise<AuthSessionCheckResult> => {
    const pendingCheck = pendingCheckRef.current
    if (pendingCheck && pendingCheck.apiBaseUrl === baseUrl) {
      return pendingCheck.promise
    }

    const promise = checkAuthSession(baseUrl).finally(() => {
      if (pendingCheckRef.current?.promise === promise) {
        pendingCheckRef.current = null
      }
    })
    pendingCheckRef.current = { apiBaseUrl: baseUrl, promise }
    return promise
  }, [])

  async function runSessionCheck(baseUrl: string, generation: number): Promise<void> {
    const isCurrent = (): boolean => generationRef.current === generation

    const result = await startSessionCheck(baseUrl)
    if (!isCurrent()) return

    if (result.kind === 'unavailable') {
      setSessionCheckFailure({ message: result.message })
      return
    }

    setSessionCheckFailure(null)

    if (result.kind === 'authenticated') {
      const { userName, userEmail, mustChangePassword } = applyAuthUserData(result.user)
      setSetupRequired(false)
      setAuthStatus(null)

      if (mustChangePassword) {
        resetProfileForm(userName, userEmail)
        setProfileStatus({
          kind: 'error',
          message: 'Для продолжения необходимо сменить пароль.'
        })
        navigate('/profile', { replace: true })
      }

      setIsAuthMetaLoaded(true)
      return
    }

    clearAuthState()

    try {
      const data = await getAuthSetupRequired(baseUrl)
      if (isCurrent()) {
        setSetupRequired(Boolean(data?.setupRequired))
        setAuthStatus(null)
      }
    } catch (error) {
      if (isCurrent()) {
        setAuthStatus({
          kind: 'error',
          message: resolveApiErrorMessage(error, baseUrl, 'Не удалось загрузить метаданные авторизации.')
        })
      }
    } finally {
      if (isCurrent()) {
        setIsAuthMetaLoaded(true)
      }
    }
  }

  const runSessionCheckRef = useRef(runSessionCheck)

  useEffect(() => {
    runSessionCheckRef.current = runSessionCheck
  })

  useEffect(() => {
    clearLegacyAuthStorage()
  }, [])

  useEffect(() => {
    const generation = generationRef.current

    function initializeAuth(): Promise<void> {
      setIsAuthMetaLoaded(false)
      setSessionCheckFailure(null)
      setIsSessionCheckRetrying(false)
      return runSessionCheckRef.current(apiBaseUrl, generation)
    }

    initializeAuth().catch((error) => {
      if (generationRef.current !== generation) return
      logUnhandledAuthEffectError(error)
    })

    return () => {
      generationRef.current += 1
    }
  }, [apiBaseUrl, setIsAuthMetaLoaded])

  const retrySessionCheck = useCallback((): void => {
    const generation = generationRef.current
    if (retryGenerationRef.current === generation || pendingCheckRef.current) return

    retryGenerationRef.current = generation
    setIsSessionCheckRetrying(true)

    runSessionCheckRef.current(apiBaseUrl, generation)
      .catch((error) => {
        if (generationRef.current !== generation) return
        logUnhandledAuthEffectError(error)
      })
      .finally(() => {
        if (retryGenerationRef.current === generation) {
          retryGenerationRef.current = null
        }
        if (generationRef.current === generation) {
          setIsSessionCheckRetrying(false)
        }
      })
  }, [apiBaseUrl])

  useEffect(() => {
    if (!sessionCheckFailure || isSessionCheckRetrying) return

    const timerId = window.setTimeout(retrySessionCheck, SESSION_CHECK_RETRY_INTERVAL_MS)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [isSessionCheckRetrying, retrySessionCheck, sessionCheckFailure])

  useEffect(() => {
    if (!isAuthenticated) return

    if (!isAdmin && pathname === '/admin') {
      navigate('/', { replace: true })
    }
  }, [isAdmin, isAuthenticated, navigate, pathname])

  return {
    isSessionCheckFailed: sessionCheckFailure !== null,
    sessionCheckErrorMessage: sessionCheckFailure?.message ?? null,
    isSessionCheckRetrying,
    retrySessionCheck
  }
}
